import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { AlertTriangle, CalendarDays, CheckCircle2, ChevronRight, Clock3, FileText, PhoneCall, Target, Users2, Car, MapPin, Navigation } from "lucide-react";
import api from "../../lib/api";
import { eur } from "../../lib/format";
import ActionLogDialog from "../actions/ActionLogDialog";

const PRIO = {
  HAUTE: "border-red-500/25 bg-red-500/5 text-red-300",
  MOYENNE: "border-amber-500/25 bg-amber-500/5 text-amber-300",
  BASSE: "border-slate-600 bg-slate-500/5 text-slate-300",
};

const TYPE_LABEL = { appel: "Appel", sms: "SMS", email: "Email", rdv: "Rendez-vous", relance: "Relance", visite: "Visite", note: "Note", autre: "Action" };
const dayLabel = (iso) => {
  if (!iso) return "—";
  const d = new Date(`${iso}T12:00:00`);
  return d.toLocaleDateString("fr-FR", { weekday: "short", day: "2-digit", month: "2-digit" });
};

function SummaryCard({ icon: Icon, label, value, tone, onClick }) {
  return (
    <button onClick={onClick} className={`text-left rounded-xl border p-4 transition-all hover:-translate-y-0.5 hover:border-slate-500 ${tone}`}>
      <div className="flex items-center justify-between">
        <span className="text-xs uppercase tracking-wider font-semibold opacity-80">{label}</span>
        <Icon className="h-4 w-4" />
      </div>
      <div className="mt-2 font-mono-metric text-3xl font-bold">{value}</div>
    </button>
  );
}

function TourneeCard({ tour, onOpen }) {
  const stats = tour?.stats || {};
  const next = tour?.prochaine_etape;

  const statusClass = (statut) => {
    if (statut === "Visitée") return "bg-emerald-500/10 text-emerald-400 border-emerald-500/20";
    if (statut === "En route") return "bg-sky-500/10 text-sky-400 border-sky-500/20";
    if (statut === "À revoir") return "bg-amber-500/10 text-amber-400 border-amber-500/20";
    if (statut === "Absent") return "bg-red-500/10 text-red-400 border-red-500/20";
    return "bg-slate-500/10 text-slate-400 border-slate-500/20";
  };

  return (
    <section className="rounded-xl bg-[#111827] border border-[#1E293B] overflow-hidden">
      <div className="px-5 py-4 border-b border-[#1E293B] flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-xl bg-sky-500/10 text-sky-400 flex items-center justify-center">
            <Car className="h-5 w-5" />
          </div>
          <div>
            <h2 className="font-head font-bold text-lg">🚗 Tournée du jour</h2>
            <p className="text-xs text-slate-500 mt-0.5">
              {tour.nom} · {tour.statut}
            </p>
          </div>
        </div>

        <button
          type="button"
          onClick={onOpen}
          className="px-3 py-2 rounded-lg border border-sky-500/25 bg-sky-500/5 text-sky-400 text-sm font-semibold hover:bg-sky-500/10"
        >
          Ouvrir la tournée
        </button>
      </div>

      <div className="p-5">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <div className="rounded-lg bg-[#0B0F17] border border-[#1E293B] p-3">
            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Progression</div>
            <div className="mt-1 text-xl font-bold text-sky-400">{stats.progression_pct || 0}%</div>
          </div>
          <div className="rounded-lg bg-[#0B0F17] border border-[#1E293B] p-3">
            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Visites</div>
            <div className="mt-1 text-xl font-bold">{stats.visitees || 0} / {stats.total || 0}</div>
          </div>
          <div className="rounded-lg bg-[#0B0F17] border border-[#1E293B] p-3">
            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">À visiter</div>
            <div className="mt-1 text-xl font-bold text-amber-400">{stats.a_visiter || 0}</div>
          </div>
          <div className="rounded-lg bg-[#0B0F17] border border-[#1E293B] p-3">
            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Rayon</div>
            <div className="mt-1 text-xl font-bold">{tour.rayon_km || 0} km</div>
          </div>
        </div>

        {next && (
          <div className="mt-4 rounded-xl border border-sky-500/20 bg-sky-500/5 p-4">
            <div className="flex items-center gap-2 text-xs uppercase tracking-wider font-bold text-sky-400">
              <Navigation className="h-4 w-4" />
              Prochaine étape
            </div>

            <div className="mt-2 flex flex-wrap items-start justify-between gap-3">
              <div>
                <div className="font-semibold text-base">{next.nom}</div>
                <div className="text-xs text-slate-400 mt-1 flex items-center gap-1">
                  <MapPin className="h-3.5 w-3.5" />
                  {next.ville || "—"} · {next.distance_km ?? "—"} km
                </div>
                <div className="text-xs text-slate-500 mt-1">
                  {next.profil_cible || next.categorie_label || "Prospect"}
                </div>
              </div>

              <span className={`text-[10px] uppercase font-bold px-2 py-1 rounded border ${statusClass(next.statut)}`}>
                {next.priorite || next.statut}
              </span>
            </div>
          </div>
        )}

        <div className="mt-4 space-y-2">
          {(tour.etapes || []).map((etape) => (
            <div
              key={etape.id || `${tour.id}-${etape.ordre}`}
              className="flex items-center gap-3 rounded-lg bg-[#0B0F17] border border-[#1E293B] px-3 py-2.5"
            >
              <div className="w-6 text-center text-xs font-bold text-slate-600">
                {etape.ordre}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-medium truncate">{etape.nom}</div>
                <div className="text-[11px] text-slate-500 truncate">
                  {etape.ville || "—"} · {etape.profil_cible || etape.categorie_label || "Prospect"}
                </div>
              </div>
              <span className={`text-[10px] uppercase font-bold px-1.5 py-0.5 rounded border ${statusClass(etape.statut)}`}>
                {etape.statut || "À visiter"}
              </span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function RelanceRow({ item, onOpen, onRelance }) {
  const overdue = item.jours_retard > 0;
  return (
    <button onClick={() => onOpen(item)} className="w-full text-left p-4 hover:bg-[#0B0F17]/70 transition-colors border-b border-[#1E293B]/60 last:border-0">
      <div className="flex items-start gap-3">
        <div className={`mt-0.5 h-8 w-8 rounded-lg flex items-center justify-center ${overdue ? "bg-red-500/10 text-red-400" : "bg-amber-500/10 text-amber-400"}`}>
          {overdue ? <AlertTriangle className="h-4 w-4" /> : <PhoneCall className="h-4 w-4" />}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-semibold truncate">{item.client_nom || "Client sans nom"}</span>
            <span className={`text-[10px] uppercase font-bold px-1.5 py-0.5 rounded border ${PRIO[item.priorite]}`}>{item.priorite}</span>
          </div>
          <div className="text-xs text-slate-500 mt-1">{item.numero || "Sans numéro"} · {item.famille || "Sans famille"}</div>
          <div className="text-xs mt-2 text-slate-400">
            {overdue ? <span className="text-red-300 font-semibold">En retard de {item.jours_retard} j</span> : item.jours_retard === 0 ? <span className="text-amber-300 font-semibold">À faire aujourd'hui</span> : <span>À faire le {dayLabel(item.date_prochaine_relance)}</span>}
            <span className="ml-2 text-slate-500">· {eur(item.montant_total)}</span>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0 mt-1">
          <button
            type="button"
            data-testid={`today-relance-${item.id}`}
            onClick={(e) => {
              e.stopPropagation();
              onRelance(item);
            }}
            className="px-2.5 py-1.5 rounded-lg border border-emerald-500/25 bg-emerald-500/5 text-emerald-400 text-xs font-semibold hover:bg-emerald-500/10 hover:text-emerald-300"
          >
            Relance effectuée
          </button>
          <ChevronRight className="h-4 w-4 text-slate-600 shrink-0" />
        </div>
      </div>
    </button>
  );
}

export default function TodayCockpit() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("priorite");
  const [relanceItem, setRelanceItem] = useState(null);

  useEffect(() => {
    const load = () => api.get("/today").then((r) => setData(r.data)).catch(() => {});
    load();
    window.addEventListener("crm:data-changed", load);
    return () => window.removeEventListener("crm:data-changed", load);
  }, []);

  const priority = useMemo(() => (data?.relances || []).filter((x) => x.jours_retard >= 0), [data]);
  const week = useMemo(() => (data?.relances || []).filter((x) => x.jours_retard < 0), [data]);
  const openDevis = (i) => {
    if (i?.id) navigate(`/devis?open=${encodeURIComponent(i.id)}`);
  };

  const openRelance = (item) => {
    if (item?.id) setRelanceItem(item);
  };

  if (!data) return <div className="text-slate-500">Chargement de Ma Journée…</div>;

  return (
    <div className="space-y-5" data-testid="today-cockpit">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 text-amber-400 text-xs font-bold uppercase tracking-[0.18em]"><Target className="h-4 w-4" /> Cockpit commercial</div>
          <h1 className="font-head text-3xl sm:text-4xl font-extrabold tracking-tight mt-1">Ma Journée</h1>
          <p className="text-slate-400 text-sm mt-1">Tout ce qui mérite ton attention, au même endroit.</p>
        </div>
        <div className="text-right text-xs text-slate-500"><div className="font-semibold text-slate-300">{dayLabel(data.date)}</div><div>Vue sur 7 jours</div></div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <SummaryCard icon={AlertTriangle} label="En retard" value={data.counts.relances_retard} tone="border-red-500/20 bg-red-500/5 text-red-300" onClick={() => setTab("priorite")} />
        <SummaryCard icon={PhoneCall} label="Aujourd'hui" value={data.counts.relances_aujourdhui} tone="border-amber-500/20 bg-amber-500/5 text-amber-300" onClick={() => setTab("priorite")} />
        <SummaryCard icon={CalendarDays} label="Cette semaine" value={data.counts.relances_semaine} tone="border-sky-500/20 bg-sky-500/5 text-sky-300" onClick={() => setTab("semaine")} />
        <SummaryCard icon={CheckCircle2} label="Actions planifiées" value={data.counts.actions_semaine} tone="border-emerald-500/20 bg-emerald-500/5 text-emerald-300" onClick={() => setTab("actions")} />
      </div>

      <div className="flex flex-wrap gap-2">
        {[['priorite', 'À traiter'], ['actions', 'Mes actions'], ['semaine', 'À venir']].map(([key, label]) => (
          <button key={key} onClick={() => setTab(key)} className={`px-3 py-2 rounded-lg text-sm font-semibold border transition-colors ${tab === key ? "bg-amber-500/10 border-amber-500/25 text-amber-300" : "bg-[#111827] border-[#1E293B] text-slate-400 hover:text-slate-200"}`}>{label}</button>
        ))}
        <button onClick={() => navigate("/actions")} className="ml-auto px-3 py-2 rounded-lg text-sm font-semibold border border-[#1E293B] bg-[#111827] text-slate-300 hover:text-white">Ouvrir toutes les actions</button>
      </div>

      {tab === "priorite" && (
        <>
          {(data.tournees_du_jour || []).map((tour) => (
            <div key={tour.id} className="mb-4">
              <TourneeCard
                tour={tour}
                onOpen={() => navigate(`/business-hunter?tour=${tour.id}`)}
              />
            </div>
          ))}

          <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
          <section className="rounded-xl bg-[#111827] border border-[#1E293B] overflow-hidden">
            <div className="px-5 py-4 border-b border-[#1E293B] flex items-center justify-between"><div><h2 className="font-head font-bold text-lg">🔴 À traiter maintenant</h2><p className="text-xs text-slate-500 mt-1">Retards + relances du jour</p></div><span className="font-mono-metric text-sm text-slate-400">{priority.length}</span></div>
            {priority.length ? priority.map((i) => <RelanceRow key={i.id} item={i} onOpen={openDevis} onRelance={openRelance} />) : <div className="p-6 text-sm text-slate-500">Rien d'urgent. Tu peux respirer 😎</div>}
          </section>
          <section className="rounded-xl bg-[#111827] border border-[#1E293B] overflow-hidden">
            <div className="px-5 py-4 border-b border-[#1E293B] flex items-center justify-between"><div><h2 className="font-head font-bold text-lg">⚡ Actions rapides</h2><p className="text-xs text-slate-500 mt-1">Accès direct aux zones de travail</p></div></div>
            <div className="p-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
              <button onClick={() => navigate("/devis")} className="p-4 rounded-lg bg-[#0B0F17] border border-[#1E293B] text-left hover:border-amber-500/30"><FileText className="h-5 w-5 text-amber-400 mb-2" /><div className="font-semibold">Mes devis</div><div className="text-xs text-slate-500 mt-1">Chiffrer, filtrer, relancer</div></button>
              <button onClick={() => navigate("/clients")} className="p-4 rounded-lg bg-[#0B0F17] border border-[#1E293B] text-left hover:border-sky-500/30"><Users2 className="h-5 w-5 text-sky-400 mb-2" /><div className="font-semibold">Mes clients</div><div className="text-xs text-slate-500 mt-1">Retrouver un client en 1 clic</div></button>
              <button onClick={() => navigate("/chantiers")} className="p-4 rounded-lg bg-[#0B0F17] border border-[#1E293B] text-left hover:border-cyan-500/30"><Target className="h-5 w-5 text-cyan-400 mb-2" /><div className="font-semibold">Mes chantiers</div><div className="text-xs text-slate-500 mt-1">Suivre les projets en cours</div></button>
              <button onClick={() => navigate("/actions")} className="p-4 rounded-lg bg-[#0B0F17] border border-[#1E293B] text-left hover:border-emerald-500/30"><PhoneCall className="h-5 w-5 text-emerald-400 mb-2" /><div className="font-semibold">Journal d'actions</div><div className="text-xs text-slate-500 mt-1">Voir l'historique commercial</div></button>
            </div>
          </section>
          </div>
        </>
      )}

      {tab === "semaine" && (
        <section className="rounded-xl bg-[#111827] border border-[#1E293B] overflow-hidden">
          <div className="px-5 py-4 border-b border-[#1E293B]"><h2 className="font-head font-bold text-lg">📅 À venir cette semaine</h2><p className="text-xs text-slate-500 mt-1">Les prochaines échéances commerciales</p></div>
          {week.length ? week.map((i) => <RelanceRow key={i.id} item={i} onOpen={openDevis} />) : <div className="p-6 text-sm text-slate-500">Aucune échéance à venir dans les 7 prochains jours.</div>}
        </section>
      )}

      {tab === "actions" && (
        <section className="rounded-xl bg-[#111827] border border-[#1E293B] overflow-hidden">
          <div className="px-5 py-4 border-b border-[#1E293B]"><h2 className="font-head font-bold text-lg">🗓️ Actions planifiées</h2><p className="text-xs text-slate-500 mt-1">Les actions ayant une prochaine échéance renseignée</p></div>
          {data.actions.length ? data.actions.map((a) => (
            <button key={a.id} onClick={() => a.devis_id ? navigate(`/devis?edit=${encodeURIComponent(a.devis_id)}`) : a.client_id ? navigate(`/clients?client=${encodeURIComponent(a.client_id)}`) : navigate('/actions')} className="w-full text-left p-4 border-b border-[#1E293B]/60 hover:bg-[#0B0F17]/70 flex items-center gap-3">
              <div className="h-8 w-8 rounded-lg bg-sky-500/10 text-sky-400 flex items-center justify-center"><Clock3 className="h-4 w-4" /></div>
              <div className="flex-1 min-w-0"><div className="font-semibold">{TYPE_LABEL[a.type] || a.type}</div><div className="text-xs text-slate-500 truncate">{a.prochaine_action || a.compte_rendu || "Action commerciale"}</div></div>
              <div className="text-right shrink-0"><div className="text-sm font-semibold text-slate-300">{dayLabel(a.date_prochaine_relance)}</div><ChevronRight className="h-4 w-4 text-slate-600 ml-auto mt-1" /></div>
            </button>
          )) : <div className="p-6 text-sm text-slate-500">Aucune action planifiée.</div>}
        </section>
      )}

    {relanceItem && (
      <ActionLogDialog
        open={!!relanceItem}
        onOpenChange={(open) => {
          if (!open) setRelanceItem(null);
        }}
        context={{
          client_id: relanceItem.client_id || null,
          client_nom: relanceItem.client_nom || "",
          project_id: relanceItem.project_id || null,
          devis_id: relanceItem.id || null,
          preset_type: "relance",
        }}
        dossiers={[]}
        onSaved={() => {
          setRelanceItem(null);
          window.dispatchEvent(new CustomEvent("crm:data-changed"));
        }}
      />
    )}
    </div>
  );
}
